import { Icon, IContextualMenuProps, PrimaryButton } from "@fluentui/react";
import React from "react";
import { addCartFunc } from "../../../carts/views/CartView";
import { primaryButtonStyles } from "../../../common/views/DefaultStyles";
import PhotoSlideshow from "../../../common/views/PhotoSlideshow";
import { Utils } from "../../../common/views/Utils";
import ManagePictureView from "../../../pictures/views/ManagerPictureView";
import { PriceDisplayType, ProductKeyModel, ProductPrintModel } from "../../models/ProductModel";
import { ProductUtils } from "../../ProductUtils";
import useProductDetailVM from "../../viewmodels/ProductDetailVM";
import ProductPickerView from "../shared/ProductPickerView";
import { UserProductPictureService } from "./ProductView";

export type ProductDetailProps = {
    product: ProductKeyModel;
    pictureService: UserProductPictureService;
    isMobile: boolean;
    addToCart: addCartFunc;
    setFavorite?: (lineId: number, isDeleted: boolean) => void;
}

export default function ProductDetailView({
    product, pictureService, isMobile, addToCart, setFavorite
}: ProductDetailProps) {
    const commandMenuProps: IContextualMenuProps = {
        shouldFocusOnMount: true,
        items: []
    };

    const hasLoaded = React.useRef(false);
    const vm = useProductDetailVM();

    React.useEffect(() => {
        if (hasLoaded.current) return;

        hasLoaded.current = true;

        vm.get(product);
        vm.getPictures(product.lineId);
    }, [vm, product]);

    const width6 = Utils.getColumnWidth(6, 12, 12);
    const width4 = Utils.getColumnWidth(4);
    const width8 = Utils.getColumnWidth(8);
    const hPadding = isMobile ? "" : "ph-10";

    const shareButton = () => {
        const line = vm.selectedLine;

        return <>
            <Icon
                iconName="Share"
                className="pointer font-large"
                onClick={() => ProductUtils.onShare(line.id)}
            />
        </>
    }

    const favoriteButton = () => {
        const line = vm.selectedLine;

        if (!setFavorite) return <></>;

        return <span className="pl-15">
            <Icon
                iconName={line.isFavorite ? "HeartFill" : "Heart"}
                className="pointer font-large"
                onClick={() => {
                    setFavorite(line.id, line.isFavorite);
                    vm.selectedLine.isFavorite = !vm.selectedLine.isFavorite;
                }}
            />
        </span>
    }

    const infoBlock = () => {
        const group = vm.group;
        const line = vm.selectedLine;
        const product = vm.selectedProduct;

        const productName = ProductUtils.getProductText(line.name, product.size?.nickName, product.code);

        return <div>
            <div className="ms-Grid-row">
                <div className={width8 + " bold"}>
                    {group.brand.name}
                </div>
                <div className={width4 + " right"}>
                    {shareButton()}
                    {favoriteButton()}
                </div>
            </div>

            {!Utils.isNullOrEmpty(group.name) && <div className="pt-5">
                <div>{group.name}</div>
            </div>}

            {!Utils.isNullOrEmpty(productName) && <div className="pt-5">
                {productName}
            </div>}

            {line.priceDisplayType !== PriceDisplayType.hide && priceBlock(product.printInfo)}

            {!Utils.isNullOrEmpty(line.style) && <div className="pt-5" >Style #: {line.style}</div>}

            {group.totalPointRate > 0 && <div className="pt-5 accepted">
                <span>Shop today and get {ProductUtils.printStarIcon()} {group.totalPointRate}</span>
                <span> x </span>
                <span>Earing Points {ProductUtils.printStarIcon()}</span>
            </div>}

            {!Utils.isNullOrEmpty(group.specification) && <div>
                <h4 className="pt-20 pb-5">Specifications:</h4>
                {ProductUtils.splitSpecification(vm.group.specification).map((line: string) => <p>{line}</p>)}
            </div>}

            {product && line.priceDisplayType === PriceDisplayType.show && <div className="pv-20">
                <PrimaryButton
                    onClick={() => addToCart(product.id, 1)}
                    style={{ width: "100%" }}
                    styles={primaryButtonStyles}
                >Add To Cart</PrimaryButton>
            </div>}
        </div>
    }

    const priceBlock = (info: ProductPrintModel) => {
        return <>
            {!Utils.isNullOrEmpty(info.price) && <>
                <div className="product-price-wrapper pv-20" style={{ display: "block" }}>
                    <div className="currency-sign">$</div>
                    <span className="product-price">{info.wholePrice}</span>
                    <div className="currency-sign" style={{ float: "left" }}>{info.decimalPrice}</div>

                    {!Utils.isNullOrEmpty(info.originalPrice) && <div className="product-full-price-wrapper">
                        <div className="product-full-price firmed">Was: ${info.originalPrice}</div>
                        <div className="product-full-price-detail">{info.saveDescription}</div>
                    </div>}
                </div>
            </>}
        </>;
    }

    const pictureView = () => {
        return <>
            <ManagePictureView
                isReadonly={true}
                key={vm.selectedKey.lineId}
                ownerId={vm.selectedKey.lineId}
                service={pictureService}
            />
        </>;
    }

    return <div className="ms-Grid ph-0">
        <div className="ms-Grid-row">
            <div className={width6 + " pt-10 " + hPadding}>
                {vm.selectedKey.lineId > 0 && vm.pictures && <>
                    <PhotoSlideshow
                        key={vm.selectedKey.lineId}
                        photos={vm.pictures}
                        aspectRatio={1}
                    />
                </>}
            </div>
            <div className={width6 + " pt-10 " + hPadding}>
                <ProductPickerView product={product} onChange={vm.changeInGroup} />
                {infoBlock()}
            </div>
        </div>
    </div>;
}